import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export function DataTableToolbar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchTestId,
  children,
  className,
}) {
  return (
    <div className={cn('rounded-2xl border border-border bg-card p-3 sm:p-4', className)}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        {onSearchChange && (
          <div className="relative w-full lg:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchValue}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={searchPlaceholder}
              className="pl-9"
              data-testid={searchTestId}
            />
          </div>
        )}
        {children && (
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

export function DataTablePagination({
  page,
  totalPages,
  totalItems,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  itemLabel = 'items',
  pageSizeTestId = 'table-page-size',
  className,
}) {
  const safeTotalPages = Math.max(totalPages || 1, 1);
  const start = totalItems > 0 ? (page - 1) * pageSize + 1 : 0;
  const end = totalItems > 0 ? Math.min(page * pageSize, totalItems) : 0;

  return (
    <div className={cn('flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between', className)}>
      <p className="text-sm text-muted-foreground">
        Showing {start}-{end} of {totalItems}{itemLabel ? ` ${itemLabel}` : ''}
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {pageSizeOptions?.length > 0 && onPageSizeChange && (
          <Select value={String(pageSize)} onValueChange={(value) => onPageSizeChange(Number(value))}>
            <SelectTrigger className="w-full sm:w-32" data-testid={pageSizeTestId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>{size} rows</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.max(page - 1, 1))}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">Page {page} of {safeTotalPages}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.min(page + 1, safeTotalPages))}
            disabled={page >= safeTotalPages}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

export function DataTableSortHeader({
  label,
  sortKey,
  activeSortKey,
  sortDirection,
  onSort,
  align = 'left',
  className,
}) {
  const isActive = activeSortKey === sortKey;
  const Icon = isActive ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={cn(
        'inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground',
        align === 'center' && 'justify-center',
        align === 'right' && 'justify-end',
        className
      )}
    >
      {label}
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

export function DataTableSkeleton({
  rows = 5,
  columns = 4,
  showToolbar = true,
  showPagination = true,
  className,
  'data-testid': dataTestId = 'data-table-loading',
}) {
  return (
    <div className={cn('space-y-4', className)} data-testid={dataTestId}>
      {showToolbar && (
        <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-3 sm:p-4 lg:flex-row lg:items-center lg:justify-between">
          <Skeleton className="h-10 w-full lg:max-w-sm" />
          <div className="flex flex-col gap-2 sm:flex-row">
            <Skeleton className="h-10 w-full sm:w-36" />
            <Skeleton className="h-10 w-full sm:w-36" />
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-border">
        <div
          className="grid gap-4 border-b border-border bg-muted/30 p-4"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={index} className="h-4 w-24" />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={rowIndex}
            className="grid gap-4 border-b border-border p-4 last:border-b-0"
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          >
            <div className="col-span-2 flex min-w-0 items-center gap-3">
              <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-40 max-w-full" />
                <Skeleton className="h-3 w-28 max-w-full" />
              </div>
            </div>
            {Array.from({ length: Math.max(columns - 2, 1) }).map((_, cellIndex) => (
              <Skeleton key={cellIndex} className="h-6 w-20 rounded-full" />
            ))}
          </div>
        ))}
      </div>

      {showPagination && (
        <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-64 rounded-xl" />
        </div>
      )}
    </div>
  );
}
